/**
 * Evaluate Documents Modal
 *
 * Runs 4 sequential real API steps:
 *  1. (Optional) Re-run extraction for single selected doc
 *  2. Trigger single-check (missing docs + field completeness)
 *  3. Cross-document rules check
 *  4. AI cross-document analysis
 *
 * Results are shown in three sections: Required Docs, Cross-Checks, AI Observations.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@/lib/toast-service';
import { Modal, ModalOverlay, Dialog } from '@/components/application/modals/modal';
import { Button } from '@/components/base/buttons/button';
import { Badge } from '@/components/base/badges/badges';
import { useContainerDocuments, useExtractDocument } from '@/hooks/api/use-documents';
import {
    useTriggerSingleCheck,
    useRunCrossChecks,
    useAICrossAnalysis,
    CONTAINER_VALIDATION_QUERY_KEYS,
    type SingleCheckResult,
    type CrossCheckRunResult,
    type AICrossAnalysisResult,
} from '@/hooks/api/use-container-validation';
import { CONTAINER_ISSUES_QUERY_KEYS } from '@/hooks/api/use-container-issues';
import {
    CheckCircle,
    XCircle,
    File06,
    Zap,
    RefreshCw01,
    CheckSquare,
    Loading01,
    Circle,
} from '@untitledui/icons';

export interface EvaluationResults {
    singleCheck: SingleCheckResult | null;
    crossCheck: CrossCheckRunResult | null;
    aiAnalysis: AICrossAnalysisResult | null;
}

interface EvaluateDocumentsModalProps {
    containerId: string;
    containerNumber: string;
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    /** When true, skip to results state with initialResults — read-only view */
    viewMode?: boolean;
    initialResults?: EvaluationResults;
}

interface DocumentItem {
    id: string;
    name: string;
    type: string;
    selected: boolean;
}

type ModalState = 'select' | 'evaluating' | 'results';

interface StepStatus {
    label: string;
    state: 'pending' | 'running' | 'done' | 'skipped';
}

const formatDocType = (type: string): string =>
    type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

const severityColor = (s: string): 'error' | 'warning' | 'gray' => {
    if (s === 'high' || s === 'critical') return 'error';
    if (s === 'medium') return 'warning';
    return 'gray';
};

export function EvaluateDocumentsModal({
    containerId,
    containerNumber,
    isOpen,
    onClose,
    onSuccess,
    viewMode = false,
    initialResults,
}: EvaluateDocumentsModalProps) {
    const { t } = useTranslation('pages:containers');
    const queryClient = useQueryClient();

    const [state, setState] = useState<ModalState>('select');
    const [documents, setDocuments] = useState<DocumentItem[]>([]);
    const [reparseEnabled, setReparseEnabled] = useState(false);

    const [steps, setSteps] = useState<StepStatus[]>([]);
    const [singleCheckResult, setSingleCheckResult] = useState<SingleCheckResult | null>(null);
    const [crossCheckResult, setCrossCheckResult] = useState<CrossCheckRunResult | null>(null);
    const [aiResult, setAiResult] = useState<AICrossAnalysisResult | null>(null);

    const { data: apiDocuments = [], isLoading: isLoadingDocuments } = useContainerDocuments(containerId);

    const extractMutation = useExtractDocument();
    const singleCheckMutation = useTriggerSingleCheck();
    const crossChecksMutation = useRunCrossChecks();
    const aiMutation = useAICrossAnalysis();

    // Reset + populate docs on open (or enter view mode with stored results)
    useEffect(() => {
        if (isOpen) {
            if (viewMode && initialResults) {
                setSingleCheckResult(initialResults.singleCheck);
                setCrossCheckResult(initialResults.crossCheck);
                setAiResult(initialResults.aiAnalysis);
                setState('results');
            } else {
                setState('select');
                setReparseEnabled(false);
                setSingleCheckResult(null);
                setCrossCheckResult(null);
                setAiResult(null);
            }
        }
    }, [isOpen, viewMode, initialResults]);

    useEffect(() => {
        if (isOpen && apiDocuments.length > 0) {
            setDocuments(
                apiDocuments.map(doc => ({
                    id: doc.document_id,
                    name: doc.file_name || 'Untitled Document',
                    type: doc.document_type || 'unknown',
                    selected: true,
                })),
            );
        }
    }, [isOpen, apiDocuments]);

    const selectedIds = documents.filter(d => d.selected).map(d => d.id);
    const singleSelected = selectedIds.length === 1;

    const buildSteps = (withReparse: boolean): StepStatus[] => {
        const list: StepStatus[] = [];
        if (withReparse) list.push({ label: t('detail.evaluation.stepReExtraction'), state: 'pending' });
        list.push({ label: t('detail.evaluation.stepRequiredDocs'), state: 'pending' });
        list.push({ label: t('detail.evaluation.stepCrossRules'), state: 'pending' });
        list.push({ label: t('detail.evaluation.stepAiAnalysis'), state: 'pending' });
        return list;
    };

    const setStepState = (idx: number, s: StepStatus['state']) => {
        setSteps(prev => prev.map((step, i) => (i === idx ? { ...step, state: s } : step)));
    };

    const handleStartEvaluation = async () => {
        if (selectedIds.length === 0) {
            toast.error(t('detail.actions.selectAtLeast1', 'Select at least 1 document'));
            return;
        }

        const withReparse = reparseEnabled && singleSelected;
        const initialSteps = buildSteps(withReparse);
        setSteps(initialSteps);
        setState('evaluating');

        let stepIdx = 0;

        try {
            // Step 1 (optional): Re-parse
            if (withReparse) {
                setStepState(stepIdx, 'running');
                await extractMutation.mutateAsync({
                    documentId: selectedIds[0],
                    request: { force_reextract: true },
                });
                setStepState(stepIdx, 'done');
                stepIdx++;
            }

            // Step 2: Single-check
            setStepState(stepIdx, 'running');
            const singleCheck = await singleCheckMutation.mutateAsync(containerId);
            setSingleCheckResult(singleCheck);
            setStepState(stepIdx, 'done');
            stepIdx++;

            // Step 3: Cross-checks
            setStepState(stepIdx, 'running');
            const crossCheck = await crossChecksMutation.mutateAsync({
                expedition_id: containerId,
                document_ids: selectedIds,
            });
            setCrossCheckResult(crossCheck);
            setStepState(stepIdx, 'done');
            stepIdx++;

            // Step 4: AI analysis — pass prior results for activity storage
            setStepState(stepIdx, 'running');
            const aiAnalysis = await aiMutation.mutateAsync({
                container_id: containerId,
                document_ids: selectedIds,
                single_check_result: singleCheck,
                cross_check_result: crossCheck,
            });
            setAiResult(aiAnalysis);
            setStepState(stepIdx, 'done');

            // Invalidate validation tab data
            queryClient.invalidateQueries({
                queryKey: CONTAINER_VALIDATION_QUERY_KEYS.overview(containerId),
            });
            // Invalidate issues so new AI-created issues appear in Issues tab
            queryClient.invalidateQueries({
                queryKey: CONTAINER_ISSUES_QUERY_KEYS.list(containerId),
            });
            // Invalidate container detail (alert_count, has_active_alerts)
            queryClient.invalidateQueries({ queryKey: ['containers', containerId] });
            // Invalidate activities so the new evaluation activity shows in Notes > Actions
            queryClient.invalidateQueries({ queryKey: ['activities'] });

            setState('results');
        } catch (err) {
            console.error('Evaluation failed:', err);
            toast.error(t('detail.actions.evaluationError', 'Document evaluation failed'));
            setState('select');
        }
    };

    const toggleDocument = (id: string) => {
        setDocuments(docs => docs.map(d => (d.id === id ? { ...d, selected: !d.selected } : d)));
    };

    const hasDocuments = documents.length > 0;

    if (!isOpen) return null;

    return (
        <ModalOverlay isOpen={isOpen} onOpenChange={open => !open && onClose()}>
            <Modal>
                <Dialog>
                    <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-w-2xl w-full">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h2 className="text-xl font-semibold text-primary">
                                    {state === 'select' && t('detail.actions.evaluateDocs', 'Evaluate Documents')}
                                    {state === 'evaluating' && t('detail.actions.evaluating', 'Evaluating Documents…')}
                                    {state === 'results' && t('detail.actions.evaluationResults', 'Evaluation Results')}
                                </h2>
                                <p className="text-sm text-tertiary mt-1">
                                    {state === 'select' && t('detail.actions.evaluateDesc', 'Select documents to cross-check for consistency')}
                                    {state === 'evaluating' && t('detail.actions.evaluatingDesc', 'Running checks…')}
                                    {state === 'results' && containerNumber}
                                </p>
                            </div>
                            <button onClick={onClose} className="text-tertiary hover:text-primary">
                                <XCircle className="size-5" />
                            </button>
                        </div>

                        {/* Body */}
                        <div className="min-h-[200px]">
                            {/* ── SELECT ── */}
                            {state === 'select' && isLoadingDocuments && (
                                <div className="flex items-center justify-center py-12">
                                    <p className="text-tertiary">{t('detail.loading', 'Loading…')}</p>
                                </div>
                            )}

                            {state === 'select' && !isLoadingDocuments && !hasDocuments && (
                                <div className="flex flex-col items-center justify-center py-12 text-center">
                                    <div className="rounded-full bg-secondary/50 p-4 mb-4">
                                        <File06 className="h-8 w-8 text-tertiary" />
                                    </div>
                                    <h3 className="text-lg font-medium text-primary mb-2">
                                        {t('detail.actions.noDocumentsToEvaluate', 'No Documents Available')}
                                    </h3>
                                    <p className="text-sm text-tertiary max-w-sm">
                                        {t('detail.actions.noDocumentsToEvaluateDesc', 'Upload documents to this container to enable evaluation.')}
                                    </p>
                                </div>
                            )}

                            {state === 'select' && !isLoadingDocuments && hasDocuments && (
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm text-tertiary">
                                            {t('detail.evaluation.selectedOf', { selected: selectedIds.length, total: documents.length })}
                                        </span>
                                    </div>

                                    {documents.map(doc => (
                                        <label
                                            key={doc.id}
                                            className={`flex items-center gap-3 p-3 rounded-lg ring-1 cursor-pointer transition-colors ${
                                                doc.selected
                                                    ? 'ring-brand-500 bg-brand-50 dark:bg-brand-900/20'
                                                    : 'ring-secondary hover:bg-secondary/30'
                                            }`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={doc.selected}
                                                onChange={() => toggleDocument(doc.id)}
                                                className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                                            />
                                            <File06 className="h-5 w-5 text-tertiary shrink-0" />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-primary truncate">{doc.name}</p>
                                                <p className="text-xs text-tertiary">{formatDocType(doc.type)}</p>
                                            </div>
                                        </label>
                                    ))}

                                    {/* Re-parse toggle (single doc only) */}
                                    {singleSelected && (
                                        <label className="flex items-center gap-3 p-3 rounded-lg ring-1 ring-secondary cursor-pointer mt-2">
                                            <input
                                                type="checkbox"
                                                checked={reparseEnabled}
                                                onChange={() => setReparseEnabled(v => !v)}
                                                className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                                            />
                                            <RefreshCw01 className="h-4 w-4 text-tertiary shrink-0" />
                                            <span className="text-sm text-secondary">
                                                {t('detail.actions.rerunnExtraction', 'Re-run extraction before analysis')}
                                            </span>
                                        </label>
                                    )}

                                    {/* What will run chip */}
                                    <p className="text-xs text-tertiary pt-1">
                                        {t('detail.evaluation.willCheck')}
                                    </p>
                                </div>
                            )}

                            {/* ── EVALUATING ── */}
                            {state === 'evaluating' && (
                                <div className="py-4 space-y-3">
                                    {steps.map((step, idx) => (
                                        <div key={idx} className="flex items-center gap-3">
                                            {step.state === 'running' && (
                                                <Loading01 className="h-5 w-5 text-brand-600 animate-spin shrink-0" />
                                            )}
                                            {step.state === 'done' && (
                                                <CheckCircle className="h-5 w-5 text-success-600 shrink-0" />
                                            )}
                                            {(step.state === 'pending' || step.state === 'skipped') && (
                                                <Circle className="h-5 w-5 text-tertiary shrink-0" />
                                            )}
                                            <span
                                                className={`text-sm ${
                                                    step.state === 'running'
                                                        ? 'text-primary font-medium'
                                                        : step.state === 'done'
                                                        ? 'text-secondary'
                                                        : 'text-tertiary'
                                                }`}
                                            >
                                                {step.label}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* ── RESULTS ── */}
                            {state === 'results' && (
                                <div className="space-y-5">
                                    {/* Required Documents */}
                                    <ResultSection title={t('detail.evaluation.requiredDocuments')}>
                                        {singleCheckResult && singleCheckResult.missing_types.length === 0 && singleCheckResult.field_issues.length === 0 ? (
                                            <SuccessRow label={t('detail.evaluation.allRequiredPresent')} />
                                        ) : (
                                            <>
                                                {singleCheckResult?.missing_types.map(type => (
                                                    <div key={type} className="flex items-center gap-2 py-1">
                                                        <Badge color="warning" size="sm">{formatDocType(type)}</Badge>
                                                        <span className="text-xs text-secondary">{t('detail.evaluation.missing')}</span>
                                                    </div>
                                                ))}
                                                {singleCheckResult?.field_issues.map((fi, i) => (
                                                    <div key={i} className="text-sm text-secondary py-1">
                                                        <span className="font-medium text-primary">Document {i + 1}:</span>{' '}
                                                        {fi.missing_critical.length} critical + {fi.missing_warning.length} warning fields missing
                                                    </div>
                                                ))}
                                                {!singleCheckResult && (
                                                    <span className="text-xs text-tertiary">{t('detail.evaluation.noData')}</span>
                                                )}
                                            </>
                                        )}
                                    </ResultSection>

                                    {/* Cross-Check Results */}
                                    <ResultSection title={t('detail.evaluation.crossCheckResults')}>
                                        {crossCheckResult ? (
                                            crossCheckResult.failed_validations === 0 ? (
                                                <SuccessRow label={t('detail.evaluation.crossChecksPassed', { count: crossCheckResult.total_rules_executed })} />
                                            ) : (
                                                <>
                                                    <p className="text-xs text-secondary mb-2">
                                                        {t('detail.evaluation.passedAndFailed', { passed: crossCheckResult.passed_validations, failed: crossCheckResult.failed_validations })}
                                                    </p>
                                                    {crossCheckResult.validation_results
                                                        .filter(r => r.status === 'failed' || r.is_valid === false)
                                                        .map((r, i) => (
                                                            <div key={i} className="flex items-start gap-2 py-1">
                                                                <Badge color={severityColor(r.severity ?? 'medium')} size="sm">
                                                                    {r.severity ?? 'medium'}
                                                                </Badge>
                                                                <span className="text-sm text-secondary">{r.rule_name ?? r.rule_code ?? 'Rule'}: {r.message}</span>
                                                            </div>
                                                        ))}
                                                </>
                                            )
                                        ) : (
                                            <span className="text-xs text-tertiary">{t('detail.evaluation.noData')}</span>
                                        )}
                                    </ResultSection>

                                    {/* AI Observations */}
                                    <ResultSection title={t('detail.evaluation.aiObservations')}>
                                        {aiResult ? (
                                            aiResult.findings.length === 0 ? (
                                                <SuccessRow label={t('detail.evaluation.noDiscrepancies')} />
                                            ) : (
                                                <>
                                                    <p className="text-xs text-secondary mb-3">{aiResult.summary}</p>
                                                    {aiResult.findings.map((f, i) => (
                                                        <div
                                                            key={i}
                                                            className={`p-3 rounded-lg ring-1 mb-2 ${
                                                                f.severity === 'high'
                                                                    ? 'bg-error-50 dark:bg-error-950/30 ring-error-200 dark:ring-error-800'
                                                                    : f.severity === 'medium'
                                                                    ? 'bg-warning-50 dark:bg-warning-950/30 ring-warning-200 dark:ring-warning-800'
                                                                    : 'bg-secondary/30 ring-secondary'
                                                            }`}
                                                        >
                                                            <div className="flex items-center gap-2 mb-1">
                                                                <Badge color={severityColor(f.severity)} size="sm">{f.severity}</Badge>
                                                                <span className="text-xs text-tertiary">{f.category.replace(/_/g, ' ')}</span>
                                                            </div>
                                                            <p className="text-sm text-primary">{f.description}</p>
                                                            {f.documents_involved.length > 0 && (
                                                                <p className="text-xs text-tertiary mt-1">
                                                                    {f.documents_involved.join(' · ')}
                                                                </p>
                                                            )}
                                                        </div>
                                                    ))}
                                                </>
                                            )
                                        ) : (
                                            <span className="text-xs text-tertiary">{t('detail.evaluation.noData')}</span>
                                        )}
                                    </ResultSection>
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="flex justify-end gap-3 mt-6">
                            {state === 'select' && (
                                <>
                                    <Button color="secondary" onClick={onClose}>
                                        {t('common:cancel', 'Cancel')}
                                    </Button>
                                    {hasDocuments && (
                                        <Button
                                            color="primary"
                                            onClick={handleStartEvaluation}
                                            iconLeading={Zap}
                                            isDisabled={selectedIds.length === 0}
                                        >
                                            {t('detail.actions.startEvaluation', 'Start Evaluation')}
                                        </Button>
                                    )}
                                </>
                            )}

                            {state === 'evaluating' && (
                                <Button color="secondary" onClick={onClose}>
                                    {t('common:cancel', 'Cancel')}
                                </Button>
                            )}

                            {state === 'results' && (
                                <>
                                    <Button color="secondary" onClick={onClose}>
                                        {t('common:close', 'Close')}
                                    </Button>
                                    {!viewMode && (
                                        <Button color="primary" iconLeading={CheckSquare} onClick={() => { onSuccess(); onClose(); }}>
                                            {t('detail.actions.viewValidationTab', 'View Validation Tab')}
                                        </Button>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </Dialog>
            </Modal>
        </ModalOverlay>
    );
}

// ── Small helper components ───────────────────────────────────────────────────

function ResultSection({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <div>
            <h4 className="text-sm font-semibold text-primary mb-2">{title}</h4>
            <div className="rounded-lg ring-1 ring-secondary p-3 space-y-1">{children}</div>
        </div>
    );
}

function SuccessRow({ label }: { label: string }) {
    return (
        <div className="flex items-center gap-2 py-1">
            <CheckCircle className="h-4 w-4 text-success-600 shrink-0" />
            <span className="text-sm text-secondary">{label}</span>
        </div>
    );
}
